import { useRef, useState, useEffect, useCallback } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mapboxgl } from './utils/mapboxConfig';
import { useMapInitialization } from './hooks/useMapInitialization';
import { reverseGeocode } from '@/lib/geocoding';
import { Loader2, MapPin } from 'lucide-react';

interface LocationMapProps {
  latitude?: number;
  longitude?: number;
  onLocationChange: (lat: number, lng: number) => void;
  onAddressChange: (address: string) => void;
  height?: string;
  className?: string;
}

const LocationMap = ({ 
  latitude, 
  longitude, 
  onLocationChange, 
  onAddressChange,
  height = '300px',
  className = ''
}: LocationMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  // Use our custom map initialization hook
  const { mapLoaded: initialized, mapError } = useMapInitialization({
    mapContainer,
    mapRef: map,
    defaultCenter: latitude && longitude ? [longitude, latitude] : [-86.7816, 36.1627], // Nashville default
    defaultZoom: 10,
    onInteraction: () => setUserInteracted(true)
  });

  // Create a draggable marker
  const createMarker = useCallback((lng: number, lat: number) => {
    if (!map.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.remove();
    }

    // Create marker element
    const el = document.createElement('div');
    el.className = 'mapboxgl-marker-anchor';
    el.style.width = '30px';
    el.style.height = '30px';
    el.style.position = 'relative';
    el.style.cursor = 'grab';

    const markerEl = document.createElement('div');
    markerEl.className = 'flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
    markerEl.innerHTML = `
      <div class="w-6 h-6 rounded-full bg-adv-orange opacity-30 absolute animate-pulse"></div>
      <div class="relative flex items-center justify-center w-6 h-6 rounded-full bg-adv-orange text-white shadow-lg">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 6.9 8 11.7z"></path>
          <circle cx="12" cy="12" r="10"></circle>
        </svg>
      </div>
    `;
    el.appendChild(markerEl);

    // Create draggable marker
    markerRef.current = new mapboxgl.Marker({
      element: el,
      anchor: 'bottom',
      draggable: true
    })
      .setLngLat([lng, lat])
      .addTo(map.current);

    // Handle marker drag events
    markerRef.current.on('dragstart', () => {
      el.style.cursor = 'grabbing';
    });

    markerRef.current.on('dragend', async () => {
      el.style.cursor = 'grab';
      
      if (markerRef.current) {
        const lngLat = markerRef.current.getLngLat();
        const lat = lngLat.lat;
        const lng = lngLat.lng;
        
        // Update coordinates
        onLocationChange(lat, lng);
        
        // Reverse geocode to get address
        setIsReverseGeocoding(true);
        try {
          const results = await reverseGeocode([lng, lat], { limit: 1 });
          if (results.length > 0) {
            onAddressChange(results[0].placeName);
          }
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
        } finally {
          setIsReverseGeocoding(false);
        }
      }
    });
  }, [onLocationChange, onAddressChange]);

  // Handle map clicks to place marker
  const handleMapClick = useCallback(async (e: mapboxgl.MapMouseEvent) => {
    if (!map.current) return;

    const { lng, lat } = e.lngLat;
    
    // Create or move marker
    createMarker(lng, lat);
    
    // Update coordinates
    onLocationChange(lat, lng);
    
    // Reverse geocode to get address
    setIsReverseGeocoding(true);
    try {
      const results = await reverseGeocode([lng, lat], { limit: 1 });
      if (results.length > 0) {
        onAddressChange(results[0].placeName);
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
    } finally {
      setIsReverseGeocoding(false);
    }
  }, [createMarker, onLocationChange, onAddressChange]);

  // Update marker when coordinates change externally
  useEffect(() => {
    if (mapLoaded && latitude && longitude) {
      createMarker(longitude, latitude);
      
      // Center map on the new location
      if (map.current) {
        map.current.flyTo({
          center: [longitude, latitude],
          zoom: 12,
          essential: true
        });
      }
    }
  }, [latitude, longitude, mapLoaded, createMarker]);

  // Set up map click handler
  useEffect(() => {
    if (mapLoaded && map.current) {
      map.current.on('click', handleMapClick);
      
      return () => {
        if (map.current) {
          map.current.off('click', handleMapClick);
        }
      };
    }
  }, [mapLoaded, handleMapClick]);

  // Update mapLoaded state when initialization completes
  useEffect(() => {
    if (initialized) {
      setMapLoaded(true);
    }
  }, [initialized]);

  return (
    <div className={`relative ${className}`} style={{ height }}>
      {mapError && (
        <div className="absolute inset-0 bg-adv-charcoal flex items-center justify-center p-4 z-10 rounded-lg">
          <div className="text-center">
            <MapPin size={32} className="text-adv-orange mb-4 mx-auto" />
            <h3 className="text-lg font-bold text-white mb-2">Map Error</h3>
            <p className="text-adv-lightGray">{mapError}</p>
          </div>
        </div>
      )}
      
      {isReverseGeocoding && (
        <div className="absolute top-4 right-4 bg-adv-charcoal/90 backdrop-blur-sm rounded-lg p-2 z-10">
          <div className="flex items-center space-x-2">
            <Loader2 size={16} className="text-adv-orange animate-spin" />
            <span className="text-white text-sm">Getting address...</span>
          </div>
        </div>
      )}
      
      <div ref={mapContainer} className="absolute inset-0 rounded-lg" />
      
      {/* Instructions overlay */}
      {mapLoaded && !mapError && (
        <div className="absolute bottom-4 left-4 right-4 bg-adv-charcoal/90 backdrop-blur-sm rounded-lg p-3 z-10">
          <p className="text-white text-sm text-center">
            Click on the map to place a pin or drag the existing pin to adjust the location
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationMap;
